<template>
  <!-- eslint-disable vue/no-v-html -->
  <section :class="customClass">
    <div v-if="isExtended" class="grouper-container">
      <div v-for="(action, index) in validatedActions" :key="index">
        <b-tooltip
          v-if="index < defaultDotsMaxActions + 1"
          :always="checkActivatedTooltip(index) && getActiveTooltip(action)"
          :position="action.position ? action.position : defaultPosition"
          :type="getTooltipColorType(action)"
          size="is-small"
          role="tooltip"
        >
          <template v-slot:content>
            <section
              @mouseout="setShowTooltip(index, false)"
              @mousemove="setShowTooltip(index, true)"
            >
              <p v-if="action.html" v-html="action.html" />
              <p v-else>
                {{ action.label }}
              </p>
            </section>
          </template>
          <section
            @mouseover="setShowTooltip(index, true)"
            @mousemove="setShowTooltip(index, false)"
          >
            <routerLink
              v-if="canAccessLink(action)"
              role="link"
              rel="search external"
              :to="action.link"
              :data-cy="action.dataCY || ''"
              :aria-description="
                action.label
                  ? `${General.ARIA_LABEL.TO_LINK} ${action.label}`
                  : ''
              "
            >
              <b-icon
                :type="getIconType(action)"
                :icon="action.icon || ''"
                :class="{ 'custom-opacity': hasOpacity(action) }"
              />
            </routerLink>
            <b-button
              v-else
              role="button"
              :data-cy="action.dataCY || ''"
              class="custom-button"
              size="is-small"
              :aria-description="
                action.label
                  ? `${General.ARIA_LABEL.ACTION_BUTTON} ${action.label}`
                  : ''
              "
              @click="validateActionCallback(action)"
            >
              <b-icon
                :type="getIconType(action)"
                :icon="action.icon || ''"
                :class="{ 'custom-opacity': hasOpacity(action) }"
              />
            </b-button>
          </section>
        </b-tooltip>
      </div>
    </div>
    <div v-else class="grouper-container">
      <div v-for="(action, index) in startActionsSliced" :key="index">
        <b-tooltip
          :key="`tooltip-${index}`"
          :always="checkActivatedTooltip(index) && getActiveTooltip(action)"
          :position="action.position ? action.position : defaultPosition"
          :type="getTooltipColorType(action)"
          size="is-small"
          role="tooltip"
        >
          <template v-slot:content>
            <section
              @mouseout="setShowTooltip(index, false)"
              @mousemove="setShowTooltip(index, true)"
            >
              <p v-if="action.html" v-html="action.html" />
              <p v-else>
                {{ action.label }}
              </p>
            </section>
          </template>
          <section
            @mouseover="setShowTooltip(index, true)"
            @mousemove="setShowTooltip(index, false)"
          >
            <routerLink
              v-if="canAccessLink(action)"
              role="link"
              rel="search external"
              :to="action.link"
              :data-cy="action.dataCY || ''"
              :aria-description="
                action.label
                  ? `${General.ARIA_LABEL.TO_LINK} ${action.label}`
                  : ''
              "
            >
              <b-icon
                :type="getIconType(action)"
                :icon="action.icon || ''"
                :class="{ 'custom-opacity': hasOpacity(action) }"
              />
            </routerLink>
            <b-button
              v-else
              role="button"
              class="custom-button"
              size="is-small"
              :data-cy="action.dataCY || ''"
              :aria-description="
                action.label
                  ? `${General.ARIA_LABEL.ACTION_BUTTON} ${action.label}`
                  : ''
              "
              @click="validateActionCallback(action)"
            >
              <b-icon
                :type="getIconType(action)"
                :icon="action.icon || ''"
                :class="{ 'custom-opacity': hasOpacity(action) }"
              />
            </b-button>
          </section>
        </b-tooltip>
      </div>
      <b-dropdown role="listbox" position="is-bottom-left">
        <template slot="trigger">
          <b-button
            class="custom-button"
            size="is-small"
            role="button"
            :aria-description="General.ARIA_LABEL.MORE_ACTIONS"
            data-cy="more-actions"
          >
            <b-icon
              icon="dots-vertical"
              :type="isContrast ? defaultDotsTypeContrast : defaultDotsType"
            />
          </b-button>
        </template>
        <b-dropdown-item
          v-for="(action, index) in endActionsSliced"
          :key="index"
          aria-role="listitem"
          :hasLink="canAccessLink(action)"
          :data-cy="action.dataCY || ''"
          @click="action.link ? () => null : validateActionCallback(action)"
        >
          <routerLink
            v-if="canAccessLink(action)"
            role="link"
            rel="search external"
            class="dropdown-item"
            :to="action.link"
            :data-cy="action.dataCY || ''"
          >
            <label>{{ action.label ? action.label : "" }}</label>
          </routerLink>
          <template v-else>
            <label>{{ action.label ? action.label : "" }}</label>
          </template>
        </b-dropdown-item>
      </b-dropdown>
    </div>
  </section>
</template>

<script>
import { mapGetters } from "vuex";
import { General } from "@/utils/constants";

export default {
  name: "ActionsGrouper",
  props: {
    actions: {
      type: Array,
      required: true,
      validator: value =>
        value.every(
          element =>
            element !== null &&
            typeof element === "object" &&
            !Array.isArray(element)
        )
    },
    defaultPosition: {
      type: String,
      default: "is-top"
    },
    defaultLabelType: {
      type: String,
      default: "is-blue-oil"
    },
    defaultLabelTypeContrast: {
      type: String,
      default: "is-white-dark"
    },
    defaultIconType: {
      type: String,
      default: "is-grey-dark"
    },
    defaultIconTypeContrast: {
      type: String,
      default: "is-white-dark"
    },
    defaultDotsType: {
      type: String,
      default: "is-grey-dark"
    },
    defaultDotsTypeContrast: {
      type: String,
      default: "is-white-dark"
    },
    defaultDotsMaxActions: {
      type: Number,
      default: 3
    },
    customClass: {
      type: String,
      default: "is-align-self-flex-end"
    }
  },

  data() {
    return {
      General,
      activatedTooltipIndex: {}
    };
  },

  computed: {
    ...mapGetters({
      isContrast: "isContrast"
    }),
    validatedActions() {
      return this.actions.filter(
        action => !("validated" in action) || action.validated === true
      );
    },
    isExtended() {
      return this.validatedActions.length <= this.defaultDotsMaxActions + 1;
    },
    startActionsSliced() {
      return this.validatedActions.slice(0, this.defaultDotsMaxActions);
    },
    endActionsSliced() {
      return this.validatedActions.slice(
        this.defaultDotsMaxActions,
        this.validatedActions.length
      );
    }
  },

  methods: {
    getActiveTooltip(action) {
      return Boolean(action.label) || Boolean(action.html);
    },
    setShowTooltip(index, status) {
      this.activatedTooltipIndex[index] = status;
      this.$forceUpdate();
    },
    checkActivatedTooltip(index) {
      return Boolean(this.activatedTooltipIndex[index]);
    },
    getIcon(action) {
      return action.iconType ? action.iconType : this.defaultIconType;
    },
    getIconContrast(action) {
      return action.iconTypeContrast
        ? action.iconTypeContrast
        : this.defaultIconTypeContrast;
    },
    getTooltipColor(action) {
      return action.labelType ? action.labelType : this.defaultLabelType;
    },
    getTooltipColorContrast(action) {
      return action.labelTypeContrast
        ? action.labelTypeContrast
        : this.defaultLabelTypeContrast;
    },
    getIconType(action) {
      return this.isContrast
        ? this.getIconContrast(action)
        : this.getIcon(action);
    },
    getTooltipColorType(action) {
      return this.isContrast
        ? this.getTooltipColorContrast(action)
        : this.getTooltipColor(action);
    },
    hasOpacity(action) {
      if (typeof action.permission === "boolean") return !action.permission;
      else return false;
    },
    validateActionCallback(action) {
      if (this.hasOpacity(action))
        this.snackToast.open({
          message: action.customPermissionMessage
            ? action.customPermissionMessage
            : General.MESSAGES.ERROR_NO_PERMISSION,
          position: "is-top",
          type: "is-danger",
          duration: 8000
        });
      else action.callback();
    },
    canAccessLink(action) {
      return Boolean(action.link) && !this.hasOpacity(action);
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables";
.grouper-container {
  display: flex;
  gap: 8px;
  margin: 5px 0;
}

.custom-button {
  background-color: transparent;
  border-color: transparent;

  &:hover {
    background-color: $color-2;
    border-color: $color-2;
  }
}

.custom-opacity {
  opacity: 0.5;
}
</style>
